import {
  queryOptions,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query'
import axios, { AxiosError } from 'axios'
import { use } from 'react'

import {
  createDebitCard,
  createOnDemandPayCard,
  deleteDebitCard,
  deleteOnDemandPayCard,
  getOnDemandPayCard,
  verifyDebitCard,
} from '@/api/card'
import { DialogContext } from '@/context'
import { formatExternalCardRequestBody } from '@/lib/format'
import { handleHttpException } from '@/lib/httpException'
import {
  getEventPropertiesFromError,
  getEventPropertiesFromHttpError,
  sendAnalyticsEvent,
} from '@/lib/mixpanel'
import {
  ConnectedCard,
  CreateDebitCardInfo,
  DebitCardType,
  VerifyCardErrorReasonCode,
} from '@/types/card'
import { OldIResponse } from '@/types/common'

import { paymentProfileQueryOptions } from './profile'

export const ON_DEMAND_PAY_CARD_QUERY = 'external-card-query'

export const onDemandPayCardQueryOptions = ({
  enabled = true,
}: {
  enabled?: boolean
} = {}) =>
  queryOptions<ConnectedCard | null>({
    queryKey: [ON_DEMAND_PAY_CARD_QUERY],
    queryFn: async () => {
      try {
        const { data } = await getOnDemandPayCard()
        return data
      } catch (error) {
        if (axios.isAxiosError(error) && error.response?.status === 404) {
          return null
        }
        throw error
      }
    },
    enabled,
  })

export const useCreateDebitCard = () => {
  const { showAlert } = use(DialogContext)
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      type,
      requestBody,
    }: {
      type: DebitCardType
      requestBody: CreateDebitCardInfo
    }) => {
      if (type === 'EWA_CARD') {
        const externalCardRequestBody =
          formatExternalCardRequestBody(requestBody)
        const { data } = await createOnDemandPayCard(externalCardRequestBody)
        return data
      } else {
        const { data } = await createDebitCard(
          requestBody as CreateDebitCardInfo
        )
        return data
      }
    },

    onSuccess: async (_, request) => {
      sendAnalyticsEvent('ACCOUNT', 'card:create:success')
      if (request.type === 'EWA_CARD') {
        queryClient.invalidateQueries({
          queryKey: [ON_DEMAND_PAY_CARD_QUERY],
        })
      } else {
        let paymentProfileUpdated = false
        while (!paymentProfileUpdated) {
          try {
            const result = await queryClient.fetchQuery(
              paymentProfileQueryOptions
            )
            if (result.active_type === 'CARD') {
              paymentProfileUpdated = true
            }
          } catch {
            paymentProfileUpdated = true
          }
        }
      }
    },

    onError: (err: AxiosError<{ message: string; reason_code: string }>) => {
      const createDebitCardEvent = 'card:create:failure'
      handleHttpException<OldIResponse>(err, {
        onHttpError: error => {
          sendAnalyticsEvent(
            'ACCOUNT',
            createDebitCardEvent,
            getEventPropertiesFromHttpError(error)
          )
        },
        onOtherError: error => {
          sendAnalyticsEvent(
            'ACCOUNT',
            createDebitCardEvent,
            getEventPropertiesFromError(error)
          )
        },
      })
      queryClient.invalidateQueries({
        queryKey: paymentProfileQueryOptions.queryKey,
      })
      const description = (err?.response?.data?.reason_code as string)
        ? err?.response?.data?.reason_code
        : 'We are unable to submit your request at this time. Please try again in a few minutes.'
      showAlert({
        title: 'Unable to submit',
        description,
        hideCancel: true,
      })
    },
  })
}

export const useDeleteDebitCard = () => {
  const { showAlert } = use(DialogContext)
  const queryClient = useQueryClient()

  const mutation = useMutation({
    mutationFn: async ({
      type,
      accountId,
    }: {
      type: DebitCardType
      accountId: number
    }) => {
      if (type === 'EWA_CARD') {
        const { data } = await deleteOnDemandPayCard()
        return data
      } else {
        const { data } = await deleteDebitCard(accountId)
        return data
      }
    },

    onSuccess: (_, payload) => {
      sendAnalyticsEvent('ACCOUNT', 'card:delete:success')
      if (payload.type === 'EWA_CARD') {
        // Immediately set cache to null to reflect deleted state
        queryClient.setQueryData([ON_DEMAND_PAY_CARD_QUERY], null)
        queryClient.invalidateQueries({
          queryKey: [ON_DEMAND_PAY_CARD_QUERY],
        })
      } else {
        queryClient.invalidateQueries({
          queryKey: paymentProfileQueryOptions.queryKey,
        })
      }
    },

    onError: (error, params) => {
      const deleteDebitCardEvent = 'card:delete:failure'
      handleHttpException<OldIResponse>(error, {
        onHttpError: error => {
          sendAnalyticsEvent(
            'ACCOUNT',
            deleteDebitCardEvent,
            getEventPropertiesFromHttpError(error)
          )
        },
        onOtherError: error => {
          sendAnalyticsEvent(
            'ACCOUNT',
            deleteDebitCardEvent,
            getEventPropertiesFromError(error)
          )
        },
      })
      showAlert({
        title: 'Error deleting card',
        description:
          'Something went wrong deleting your card. Please try again.',
        cancelDescription: 'No, cancel',
        actionDescription: 'Try again',
        handleAction: () => {
          mutation.mutate(params)
        },
      })
    },
  })
  return mutation
}

const generateVerifyCardErrorMessage = (
  reasonCode: VerifyCardErrorReasonCode
) => {
  switch (reasonCode) {
    case VerifyCardErrorReasonCode.UNSUPPORTED_CARD_TYPE:
      return {
        title: 'Unable to verify card',
        description: `Please double-check that the card information is accurate and that it's either a Visa or Mastercard.`,
      }
    case VerifyCardErrorReasonCode.NOT_DEBIT_CARD:
      return {
        title: 'Unsupported card type.',
        description:
          'The connected card must be a debit card. Credit cards cannot be linked. Please enter a different card.',
      }
    case VerifyCardErrorReasonCode.INVALID_CARD_DETAILS:
      return {
        title: 'Invalid card details',
        description:
          'The card details you entered are invalid. Please try again.',
      }
    default:
      return {
        title: 'Submission Error',
        description:
          'We are unable to submit your request at this time. Please try again in a few minutes.',
      }
  }
}

export const useVerifyDebitCard = () => {
  const { showAlert } = use(DialogContext)
  return useMutation({
    mutationFn: async ({
      requestBody,
    }: {
      requestBody: CreateDebitCardInfo
    }) => {
      const { data } = await verifyDebitCard(requestBody)
      return data
    },

    onError: (err: AxiosError<{ message: string; reason_code: string }>) => {
      const { title, description } = generateVerifyCardErrorMessage(
        err?.response?.data?.reason_code as VerifyCardErrorReasonCode
      )
      showAlert({
        title: title,
        description: description,
        hideCancel: true,
      })
    },
  })
}
